import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth, usePermissions } from '@/lib/auth';
import type { PermissionCode } from '@/lib/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { useT } from '@/lib/i18n';

interface ProtectedRouteProps {
  /** Достаточно любого из перечисленных прав. Пустой список — только авторизация. */
  permissions?: readonly (PermissionCode | string)[];
  /** Требовать все права сразу (по умолчанию — любое). */
  requireAll?: boolean;
  children?: React.ReactNode;
}

/**
 * Защита маршрута: сначала проверяем сессию, затем права.
 * Неавторизованного отправляем на /login и запоминаем, куда он шёл.
 */
export function ProtectedRoute({
  permissions = [],
  requireAll = false,
  children,
}: ProtectedRouteProps): React.JSX.Element {
  const { isAuthenticated, isLoading } = useAuth();
  const { canAny, canAll } = usePermissions();
  const location = useLocation();
  const t = useT();

  if (isLoading) {
    return (
      <div className="flex h-full min-h-64 w-full items-center justify-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const allowed = permissions.length === 0 || (requireAll ? canAll(permissions) : canAny(permissions));

  if (!allowed) {
    return (
      <EmptyState
        icon={ShieldAlert}
        tone="warning"
        title={t('error.forbidden.title')}
        description={t('error.forbidden.text')}
      />
    );
  }

  return <>{children ?? <Outlet />}</>;
}

/** Показывает содержимое только при наличии права — для кнопок и пунктов меню. */
export function Can({
  permission,
  fallback = null,
  children,
}: {
  permission: PermissionCode | string | readonly (PermissionCode | string)[];
  fallback?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  const { can, canAny } = usePermissions();
  const allowed = Array.isArray(permission) ? canAny(permission) : can(permission as string);
  return <>{allowed ? children : fallback}</>;
}
